import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { Injectable, NgModule } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule, HammerGestureConfig, HammerModule, HAMMER_GESTURE_CONFIG } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { DefaultUrlSerializer, RouterModule, UrlSerializer, UrlTree } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import * as Hammer from 'hammerjs';
import { NgxCurrencyModule } from 'ngx-currency';
import { PerfectScrollbarConfigInterface, PerfectScrollbarModule, PERFECT_SCROLLBAR_CONFIG } from 'ngx-perfect-scrollbar';
import { QuillModule } from 'ngx-quill';
import { environment } from '../environments/environment';
import { HeaderService } from '../services/header.service';
import { SharedService } from './../services/shared.service';
import { AppComponent } from './app.component';
import { AppRoutes } from './app.routing';
import { DataService } from './data.service';
import { DemoMaterialModule } from './demo-material-module';
import { GraphQLModule } from './graphql.module';
import { AppBlankComponent } from './layouts/blank/blank.component';
import { FullComponent } from './layouts/full/full.component';
import { AppSidebarComponent } from './layouts/full/sidebar/sidebar.component';
import { SubheaderComponent } from './layouts/full/subheader/subheader.component';
import { ActivityDetailDialog } from './shared/dialog/activity-detail-dialog/activity-detail-dialog';
import { AttachmentDialog } from './shared/dialog/attachment-dialog/attachment-dialog';
import { ConfirmationDialog } from './shared/dialog/confirmation-dialog/confirmation-dialog';
import { EditServiceRequestDialog } from './shared/dialog/edit-service-request-dialog/edit-service-request-dialog';
import { NotificationDialogComponent } from './shared/dialog/notification-dialog/notification-dialog';
import { PopupDialogComponent } from './shared/dialog/popup-dialog/popup-dialog';
import { QuotationDialog } from './shared/dialog/quotation-dialog/quotation-dialog';
import { RemarkDialogComponent } from './shared/dialog/remark-dialog/remark-dialog';
import { SanitizerPipeModule } from './shared/pipe/sanitizer-pipe/sanitizer-pipe.module';
import { ProfileService } from './shared/ProfileService/profile.service';
import { SharedModule } from './shared/shared.module';
import { SpinnerComponent } from './shared/spinner.component';
import { ACLService } from '../services/acl.service';
import { CryptoService } from '../services/crypto.service';
import { SocketIoService } from '../services/socketio.service';
import { DatePipe } from '@angular/common';
import { FooterComponent } from './layouts/full/footer/footer.component';
import { PopupMenuComponent } from './layouts/full/popup-menu/popup-menu.component';
import { DynamicPipeModule } from './template/pipe/dynamic-pipe.module';
import { FooterService } from '../services/footer.service';
import { DialogHeader } from './shared/dialog/dialog-header-template/dialog-header';

export const MY_FORMATS = {
    parse: {
      dateInput: environment.InputDateFormat,
    },
    display: {
      dateInput: environment.InputDateFormat,
      monthYearLabel: "MMM YYYY",
      dateA11yLabel: "LL",
      monthYearA11yLabel: "MMMM YYYY",
    },
  };
  
@Injectable()
export class MyHammerConfig extends HammerGestureConfig {
    overrides = {
        // override hammerjs default configuration
        swipe: { direction: Hammer.DIRECTION_ALL }
    } as any;
}

export class LowerCaseUrlSerializer extends DefaultUrlSerializer {
    parse(url: string): UrlTree {
        const subStr = url.split("?");

        if (subStr.length > 1) {
            // only make url before query param to lower case
            // all query params remain allow CAPS
            return super.parse(subStr[0].toLowerCase() + "?" + subStr[1]);
        }

        return super.parse(url.toLowerCase());
    }
}

const DEFAULT_PERFECT_SCROLLBAR_CONFIG: PerfectScrollbarConfigInterface = {
    suppressScrollX: true,
    wheelSpeed: 2,
    wheelPropagation: true
};

@NgModule({
    entryComponents: [
        PopupDialogComponent,
        RemarkDialogComponent,
        ConfirmationDialog,
        AttachmentDialog,
        ActivityDetailDialog,
        QuotationDialog,
        NotificationDialogComponent,
        EditServiceRequestDialog
    ],
    declarations: [
        AppComponent,
        AppBlankComponent,
        AppSidebarComponent,
        SpinnerComponent,
        FullComponent,
        SubheaderComponent,
        PopupDialogComponent,
        RemarkDialogComponent,
        ConfirmationDialog,
        AttachmentDialog,
        ActivityDetailDialog,
        QuotationDialog,
        NotificationDialogComponent,
        EditServiceRequestDialog,
        FooterComponent,
        PopupMenuComponent,
        DialogHeader
    ],
    imports: [
        CommonModule,
        BrowserModule,
        BrowserAnimationsModule,
        DemoMaterialModule,
        NgxCurrencyModule,
        FormsModule,
        FlexLayoutModule,
        HttpClientModule,
        PerfectScrollbarModule,
        ReactiveFormsModule,
        SharedModule,
        HammerModule,
        SanitizerPipeModule,
        QuillModule.forRoot({ theme: 'snow' }),
        RouterModule.forRoot(AppRoutes, { scrollPositionRestoration: 'top' }),
        ServiceWorkerModule.register('ngsw-worker.js', { enabled: environment.production }),
        GraphQLModule,
        DynamicPipeModule
    ],
    providers: [
        {
            provide: PERFECT_SCROLLBAR_CONFIG,
            useValue: DEFAULT_PERFECT_SCROLLBAR_CONFIG,
        },
        SharedService,
        HeaderService,
        ProfileService,
        ACLService,
        CryptoService,
        SocketIoService,
        {
            provide: HAMMER_GESTURE_CONFIG,
            useClass: MyHammerConfig
        },
        DataService,
        {
            provide: UrlSerializer,
            useClass: LowerCaseUrlSerializer
        },
        DatePipe,
        FooterService
    ],

    bootstrap: [AppComponent]
})
export class AppModule { }
